import React, { useEffect, useState, useCallback } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Users, School, BadgeCheck, FileText, CreditCard,
  Receipt, BarChart3, Settings, Bell, LogOut, ShieldCheck, Menu, X, Building2,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { ROLE_LABELS, formatDateTime } from "../lib/format";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { Avatar, AvatarFallback } from "./ui/avatar";
import { Button } from "./ui/button";

const CENTRAL = ["principal_admin", "finance_agent"];

function navFor(user) {
  if (!user) return [];
  if (CENTRAL.includes(user.role)) {
    return [
      { to: "/", icon: LayoutDashboard, label: "Tableau de bord", end: true },
      { to: "/clients", icon: Users, label: "Clients & groupes" },
      ...(user.role === "principal_admin" ? [{ to: "/utilisateurs", icon: Users, label: "Utilisateurs" }] : []),
      { to: "/etablissements", icon: School, label: "Etablissements" },
      { to: "/abonnements", icon: BadgeCheck, label: "Abonnements" },
      { to: "/factures", icon: FileText, label: "Factures" },
      { to: "/paiements", icon: CreditCard, label: "Paiements" },
      { to: "/recus", icon: Receipt, label: "Recus" },
      { to: "/rapports", icon: BarChart3, label: "Rapports" },
      { to: "/parametres", icon: Settings, label: "Parametres" },
    ];
  }
  return [
    { to: "/", icon: LayoutDashboard, label: "Tableau de bord", end: true },
    { to: `/clients/${user.customer_id}`, icon: Building2, label: "Mon compte" },
    { to: "/factures", icon: FileText, label: "Factures" },
    { to: "/paiements", icon: CreditCard, label: "Paiements" },
    { to: "/recus", icon: Receipt, label: "Recus" },
    { to: "/parametres", icon: Settings, label: "Parametres" },
  ];
}

function NotificationsBell() {
  const [data, setData] = useState({ items: [], unread: 0 });
  const load = useCallback(() => {
    api.get("/notifications").then((r) => setData(r.data)).catch(() => {});
  }, []);
  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [load]);
  const markAll = async () => {
    await api.post("/notifications/read-all");
    load();
  };
  const kindDot = { approved: "bg-emerald-500", rejected: "bg-rose-500", overdue: "bg-red-500", pending: "bg-amber-500", info: "bg-sky-500" };
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button data-testid="notifications-bell" className="relative rounded-full p-2 hover:bg-slate-100 transition-colors">
          <Bell className="h-5 w-5 text-slate-600" />
          {data.unread > 0 && (
            <span data-testid="notifications-unread" className="absolute -top-0.5 -right-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-rose-500 px-1 text-[10px] font-bold text-white">
              {data.unread}
            </span>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80 bg-white shadow-xl border border-slate-200 z-50">
        <div className="flex items-center justify-between px-2 py-1.5">
          <DropdownMenuLabel className="p-0">Notifications</DropdownMenuLabel>
          {data.unread > 0 && (
            <button data-testid="mark-all-read" onClick={markAll} className="text-xs text-teal-600 hover:underline">
              Tout marquer comme lu
            </button>
          )}
        </div>
        <DropdownMenuSeparator />
        <div className="max-h-80 overflow-y-auto">
          {data.items.length === 0 && <p className="px-3 py-6 text-center text-sm text-slate-400">Aucune notification</p>}
          {data.items.slice(0, 15).map((n) => (
            <div key={n.id} className={`flex gap-2.5 px-3 py-2.5 text-sm ${n.read ? "opacity-60" : "bg-slate-50"}`}>
              <span className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${kindDot[n.kind] || "bg-slate-400"}`} />
              <div>
                <p className="font-medium text-slate-800">{n.title}</p>
                <p className="text-slate-500 text-xs leading-snug">{n.message}</p>
                <p className="text-slate-400 text-[11px] mt-0.5">{formatDateTime(n.created_at)}</p>
              </div>
            </div>
          ))}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const items = navFor(user);
  const initials = (user?.name || "?").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();

  const doLogout = async () => {
    await logout();
    navigate("/login");
  };

  const Sidebar = (
    <aside className="flex h-full w-64 shrink-0 flex-col bg-[#081326] text-slate-100">
      <div className="flex flex-col items-start gap-1 border-b border-slate-800 px-6 py-5">
          <img
            src="/scolaris-logo-light.svg"
            alt="Scolaris Sénégalsoft"
            className="block h-auto w-full max-w-[204px]"
          />
          <div className="ml-[65px]">
          <p className="text-xs font-semibold text-teal-300">
              Central
          </p>
          <p className="text-[10px] leading-tight text-slate-400">
            Administration commerciale
          </p>
          </div>
        </div>
      <nav className="flex-1 space-y-1 px-3 py-4 overflow-y-auto">
        {items.map((it) => (
          <NavLink
            key={it.to}
            to={it.to}
            end={it.end}
            onClick={() => setOpen(false)}
            data-testid={`nav-${it.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
            className={({ isActive }) => `scl-sidebar-link ${isActive ? "active" : ""}`}
          >
            <it.icon className="h-[18px] w-[18px]" />
            {it.label}
          </NavLink>
        ))}
      </nav>
      <div className="border-t border-slate-800 p-4">
        <div className="rounded-lg bg-slate-900 px-3 py-2.5 text-xs">
          <p className="text-slate-400">Connecte en tant que</p>
          <p className="font-semibold text-teal-300">{ROLE_LABELS[user?.role]}</p>
        </div>
      </div>
    </aside>
  );

  return (
    <div className="flex h-screen overflow-hidden bg-slate-50">
      <div className="hidden lg:block">{Sidebar}</div>
      {open && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div className="absolute inset-0 bg-slate-950/60" onClick={() => setOpen(false)} />
          <div className="absolute left-0 top-0 h-full">{Sidebar}</div>
        </div>
      )}
      <div className="flex flex-1 flex-col overflow-hidden">
        <header className="flex items-center justify-between border-b border-slate-200 bg-white/90 backdrop-blur-md px-4 sm:px-6 py-3">
          <div className="flex items-center gap-3">
            <button className="lg:hidden rounded-md p-2 hover:bg-slate-100" onClick={() => setOpen(!open)} data-testid="menu-toggle">
              {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
            <span className="hidden sm:inline-flex items-center gap-1.5 rounded-full bg-teal-50 px-3 py-1 text-xs font-semibold text-teal-700 border border-teal-200">
              <ShieldCheck className="h-3.5 w-3.5" /> {ROLE_LABELS[user?.role]}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <NotificationsBell />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="user-menu" className="flex items-center gap-2 rounded-full py-1 pl-1 pr-2 hover:bg-slate-100 transition-colors">
                  <Avatar className="h-8 w-8">
                    <AvatarFallback className="bg-slate-900 text-white text-xs font-semibold">{initials}</AvatarFallback>
                  </Avatar>
                  <span className="hidden sm:block text-sm font-medium text-slate-700">{user?.name}</span>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56 bg-white shadow-xl border border-slate-200 z-50">
                <DropdownMenuLabel>
                  <p className="font-semibold">{user?.name}</p>
                  <p className="text-xs font-normal text-slate-500">{user?.email}</p>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate("/parametres")} data-testid="menu-settings">
                  <Settings className="mr-2 h-4 w-4" /> Parametres
                </DropdownMenuItem>
                <DropdownMenuItem onClick={doLogout} data-testid="logout-btn" className="text-rose-600 focus:text-rose-600">
                  <LogOut className="mr-2 h-4 w-4" /> Se deconnecter
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>
        <main className="flex-1 overflow-y-auto">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 py-8">{children}</div>
        </main>
      </div>
    </div>
  );
}
